import React, { useCallback, useEffect, type FC, type ReactNode } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useOverlayContext } from '@providers/OverlayProvider';
import { useAssistant } from '@core/assistant/providers/Provider';
import { findScriptForRegion } from '@core/assistant/providers/providerUtils';
import { isMapFeatureFlagEnabled } from '@helpers/events';
import OVERLAYS from '@constants/overlays';

import { EditorNodeKind, getSpotType, SpotType } from '../editorCategories';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import { useEditorMode } from '../EditorModeContext';
import { useEditorStyles } from '../styles';
import { useDrift } from '../DriftContext';
import { useEditorAiData } from '../AiScripts/EditorAiDataContext';
import { useRegionIndex } from '../AiScripts/useRegionIndex';
import MapColorsInspector from './MapColorsInspector';
import SpotInspector from './SpotInspector';
import HotspotInspector from './HotspotInspector';
import AIScriptInspector from './AIScriptInspector';
import MapEditorInspector from './MapEditorInspector';
import {
  blinkSpotInUnreal,
  teleportToSpotInUnreal,
} from '../brandingSpotsUnreal';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }
  if (type === 'QR') {
    return <QrCodeOutlinedIcon sx={sx} />;
  }
  return <ImageOutlinedIcon sx={sx} />;
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const driftChipStyles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    padding: '2px 7px',
    borderRadius: '6px',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  missing: {
    backgroundColor: 'rgba(245, 166, 35, 0.18)',
    color: '#f0b357',
  },
  fresh: {
    backgroundColor: 'rgba(124, 108, 240, 0.22)',
    color: '#c8bdff',
  },
  orphaned: {
    backgroundColor: 'rgba(255, 77, 77, 0.18)',
    color: '#ef8b86',
  },
};

const Inspector = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const { selectedNode, clearSelection } = useEditorMode();
  const { spotDrift } = useDrift();
  const {
    roomSpots,
    brandingSpots,
    brandingHotspots,
    currentRoom,
    currentEvent,
    currentBrandingId,
    isBrandingModeEnabled,
    selectedCustomizationTargetSize,
    setSelectedCustomizationUeId,
    setIsEditorInspectorOpen,
    requestAssistantRegionChange,
    suppressNextCustomizationSelect,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { isOverlayCurrentlyOpen } = useOverlayContext();
  const { scripts } = useEditorAiData();
  const assistant = useAssistant();
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const regionIndex = useRegionIndex(mapName);

  // Notify the rest of the app (specifically the Assistant wrapper) when the
  // inspector is visible so Noa hides itself for the duration. Cleanup restores
  // the assistant when EditorMode unmounts.
  const isInspectorVisible = !!selectedNode;
  useEffect(() => {
    setIsEditorInspectorOpen(isInspectorVisible);
    return () => {
      setIsEditorInspectorOpen(false);
    };
  }, [isInspectorVisible, setIsEditorInspectorOpen]);

  const isBlockingOverlayOpen =
    isOverlayCurrentlyOpen(OVERLAYS.ADMIN_TOOLS) ||
    isOverlayCurrentlyOpen(OVERLAYS.PROFILE);
  useEffect(() => {
    if (isBlockingOverlayOpen && selectedNode) {
      clearSelection();
      setSelectedCustomizationUeId(null);
    }
  }, [
    isBlockingOverlayOpen,
    selectedNode,
    clearSelection,
    setSelectedCustomizationUeId,
  ]);

  const handleCloseInspector = () => {
    clearSelection();
    setSelectedCustomizationUeId(null);
  };

  const spotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : null;
  const spotDriftRow = spotUeId
    ? spotDrift.rows.find(
        (row) =>
          normalizeUeIdentifier(row.ueIdentifier) ===
          normalizeUeIdentifier(spotUeId),
      ) ?? null
    : null;
  const isOrphanedSpot = spotDriftRow?.status === 'orphaned';
  const isMissingSpot = spotDriftRow?.status === 'missing';
  const mapId = currentRoom?.map?.mapId;
  const canTeleportToSpot = isMapFeatureFlagEnabled(
    currentEvent,
    mapId,
    'TELEPORT_UE_ACTION',
  );
  const canBlinkSpot = isMapFeatureFlagEnabled(
    currentEvent,
    mapId,
    'RUNTIME_SPOT_UPDATE_UE',
  );
  const canUseMapEditor = useCanUseMapEditor();

  const handleTeleportToSpot = useCallback(() => {
    if (!spotUeId) {
      return;
    }
    teleportToSpotInUnreal(sendMessageToUnreal, spotUeId);

    // If the spot's owning region has an AI script attached, surface it the
    // same way walking into that region in-world would — the Inspector pane
    // occupies the same screen area as Noa, so it must close first. Mirrors
    // AiInspectorHeader's teleport-to-trigger handling: UE's region-enter
    // overlap event doesn't reliably fire for a teleport, so we notify the
    // assistant of the destination directly instead of waiting for it.
    // Provider.tsx's pendingAssistantRegionTrigger replay effect force-opens
    // the assistant as part of handling this (bypassing the branding-mode
    // guard, since this is a deliberate admin action) — no separate
    // requestAssistantOpen() call needed here.
    const regionName = regionIndex.spotToRegion[spotUeId.toLowerCase()];
    if (regionName && findScriptForRegion(scripts, regionName)) {
      // Unreal echoes CUSTOMISATION_ITEM_SELECTED for this spot shortly
      // after the teleport lands — without this, UESelectionBridge would
      // re-select it and reopen the pane we're about to close.
      suppressNextCustomizationSelect(spotUeId);
      clearSelection();
      setSelectedCustomizationUeId(null);
      requestAssistantRegionChange(regionName);
    }
  }, [
    spotUeId,
    sendMessageToUnreal,
    regionIndex,
    scripts,
    clearSelection,
    setSelectedCustomizationUeId,
    requestAssistantRegionChange,
    suppressNextCustomizationSelect,
  ]);

  const handleBlinkSpot = useCallback(() => {
    if (!spotUeId) {
      return;
    }
    blinkSpotInUnreal(sendMessageToUnreal, spotUeId);
  }, [spotUeId, sendMessageToUnreal]);

  let titleIcon: React.ReactNode = null;
  let titleText = '';
  let subtitleText: string | null = null;
  let body: React.ReactNode = null;

  if (selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS) {
    titleIcon = <PaletteOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = translate('editorMode.mapAndColors');
    body = <MapColorsInspector />;
  } else if (selectedNode?.kind === EditorNodeKind.SPOT) {
    const ueId = selectedNode.ueIdentifier;
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    const selectedUeId = normalizeUeIdentifier(ueId);
    const spot = Array.isArray(source)
      ? (source as Array<Record<string, unknown>>).find(
          (s) =>
            typeof (s as { ueIdentifier?: unknown }).ueIdentifier ===
              'string' &&
            normalizeUeIdentifier(
              (s as { ueIdentifier: string }).ueIdentifier,
            ) === selectedUeId,
        )
      : null;
    const type = getSpotType(spot ?? {});
    const name =
      typeof (spot as { name?: unknown } | null)?.name === 'string'
        ? ((spot as { name: string }).name as string)
        : ueId;
    titleIcon = getSpotTypeIcon(type, styles.panelTitleIcon);
    titleText = name;
    subtitleText = ueId;
    body = <SpotInspector key={ueId} ueIdentifier={ueId} />;
  } else if (selectedNode?.kind === EditorNodeKind.HOTSPOT) {
    const hotspotId = selectedNode.hotspotId;
    const source = Array.isArray(brandingHotspots) ? brandingHotspots : [];
    const hotspot = (source as Array<Record<string, unknown>>).find(
      (h) =>
        (h as { brandingHotspotId?: unknown }).brandingHotspotId === hotspotId,
    );
    const is3D =
      String((hotspot?.type as string) ?? '').toUpperCase() === 'OBJECT_3D';
    const hotspotName =
      typeof hotspot?.name === 'string' && hotspot.name.length > 0
        ? (hotspot.name as string)
        : hotspotId;
    titleIcon = is3D ? (
      <ExtensionOutlinedIcon sx={styles.panelTitleIcon} />
    ) : (
      <PlaceOutlinedIcon sx={styles.panelTitleIcon} />
    );
    titleText = hotspotName;
    subtitleText = hotspotId;
    body = <HotspotInspector key={hotspotId} hotspotId={hotspotId} />;
  } else if (
    canUseMapEditor &&
    selectedNode?.kind === EditorNodeKind.MAP_LAYOUT
  ) {
    titleIcon = <ViewQuiltOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = translate('editorMode.focusMapEditor', 'Map editor');
    body = <MapEditorInspector />;
  }

  // Must run before any early return — React requires the hooks order to be
  // stable across renders. REGION_SCRIPT switches to a different render branch
  // below, so this useCallback has to live above that branch as well.
  const handleCopySubtitle = useCallback(async () => {
    if (!subtitleText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(subtitleText);
    } catch (copyError) {
      console.warn(
        `Failed to copy identifier: ${(copyError as Error)?.message}`,
        { ueIdentifier: subtitleText },
      );
    }
  }, [subtitleText]);

  const spotDimensionsLabel =
    selectedNode?.kind === EditorNodeKind.SPOT &&
    selectedCustomizationTargetSize &&
    selectedCustomizationTargetSize.width > 0 &&
    selectedCustomizationTargetSize.height > 0
      ? `${selectedCustomizationTargetSize.width} × ${selectedCustomizationTargetSize.height}`
      : null;

  // REGION_SCRIPT renders its own glass panel + header (direction B design),
  // so it bypasses the shared chrome below.
  if (selectedNode?.kind === EditorNodeKind.REGION_SCRIPT) {
    return (
      <AnimatePresence>
        <Box key="inspector-ai" sx={styles.inspectorWrap}>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ width: '100%' }}
          >
            <AIScriptInspector
              key={`script-${selectedNode.scriptId ?? selectedNode.trigger}`}
              trigger={selectedNode.trigger}
              scriptId={selectedNode.scriptId}
            />
          </motion.div>
        </Box>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {selectedNode && (
        <Box key="inspector" sx={styles.inspectorWrap}>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ width: '100%' }}
          >
            <Box
              sx={[
                styles.glassPanel,
                {
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 208px)',
                  overflow: 'hidden',
                },
              ]}
            >
              <Box sx={styles.panelHeader}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box sx={[styles.panelTitleRow, { minWidth: 0 }]}>
                    {titleIcon}
                    <Tooltip
                      title={titleText}
                      placement="bottom"
                      enterDelay={400}
                    >
                      <Typography
                        sx={[
                          styles.panelTitle,
                          {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          },
                        ]}
                      >
                        {titleText}
                      </Typography>
                    </Tooltip>
                    {spotUeId &&
                    spotDriftRow &&
                    (isMissingSpot || isOrphanedSpot) ? (
                      <Box
                        component="span"
                        sx={[
                          driftChipStyles.base,
                          isOrphanedSpot
                            ? driftChipStyles.orphaned
                            : spotDriftRow.isNew
                            ? driftChipStyles.fresh
                            : driftChipStyles.missing,
                        ]}
                      >
                        {isOrphanedSpot
                          ? translate('editorMode.driftChipOrphaned')
                          : spotDriftRow.isNew
                          ? translate('editorMode.driftChipNew')
                          : translate('editorMode.driftChipMissing')}
                      </Box>
                    ) : null}
                  </Box>
                  {subtitleText && (
                    <Box sx={styles.panelSubtitleRow}>
                      <Tooltip
                        title={subtitleText}
                        placement="bottom"
                        enterDelay={400}
                      >
                        <Typography sx={styles.panelSubtitle}>
                          {subtitleText}
                        </Typography>
                      </Tooltip>
                      <IconButton
                        onClick={handleCopySubtitle}
                        sx={styles.panelSubtitleCopy}
                        size="small"
                        aria-label={translate(
                          'editorMode.copyIdentifier',
                          'Copy identifier',
                        )}
                        data-testid="inspector-copy-id-btn"
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      {spotDimensionsLabel && (
                        <Tooltip
                          title={translate('editorMode.surfaceSize')}
                          placement="bottom"
                          enterDelay={400}
                        >
                          <Typography
                            component="span"
                            sx={styles.panelSubtitleSize}
                          >
                            {spotDimensionsLabel}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
                {spotUeId && canTeleportToSpot && (
                  <Tooltip
                    title={translate('editorMode.teleportToSpot')}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <span>
                      <IconButton
                        onClick={handleTeleportToSpot}
                        sx={styles.panelIconButton}
                        size="small"
                        disabled={isOrphanedSpot}
                        aria-label={translate(
                          'editorMode.teleportToSpot',
                          'Teleport to spot',
                        )}
                        data-testid="inspector-teleport-btn"
                      >
                        <MyLocationOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {spotUeId && canBlinkSpot && (
                  <Tooltip
                    title={translate('editorMode.highlightSpot')}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <span>
                      <IconButton
                        onClick={handleBlinkSpot}
                        sx={styles.panelIconButton}
                        size="small"
                        disabled={isOrphanedSpot}
                        aria-label={translate(
                          'editorMode.highlightSpot',
                          'Highlight spot',
                        )}
                        data-testid="inspector-blink-btn"
                      >
                        <CenterFocusStrongOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <IconButton
                  onClick={handleCloseInspector}
                  sx={styles.panelIconButton}
                  size="small"
                  aria-label={translate('editorMode.close', 'Close')}
                  data-testid="inspector-close-btn"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={styles.panelBody}>{body}</Box>
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default Inspector;
